// shell/components/entity-fields/form-renderers/date-input.ts

import { createElement } from '../../../utils/dom';
import type { DateFormField } from '../../entity-view/types';
import type { FormRendererResult } from './types';

/**
 * Renders a date/datetime input.
 */
export function renderDateInput<T>(
    field: DateFormField<T>,
    initialValue: unknown,
    onChange: (value: string | null) => void
): FormRendererResult {
    const container = createElement('div', {
        className: 'ev-field-input ev-field-input--date',
    });

    const inputType = field.withTime ? 'datetime-local' : 'date';

    const inputEl = createElement('input', {
        className: 'ev-input ev-input--date',
        attributes: {
            type: inputType,
            ...(field.minDate ? { min: formatDateForInput(field.minDate, field.withTime) } : {}),
            ...(field.maxDate ? { max: formatDateForInput(field.maxDate, field.withTime) } : {}),
            ...(field.disabled ? { disabled: '' } : {}),
            ...(field.required ? { 'aria-required': 'true' } : {}),
        },
    }) as HTMLInputElement;

    // Set initial value
    if (initialValue != null) {
        const date = parseDate(initialValue);
        if (date) {
            inputEl.value = formatDateForInput(date, field.withTime);
        }
    }

    // Handle change
    const handleInput = () => {
        if (inputEl.value === '') {
            onChange(null);
        } else {
            // Return ISO string for consistency
            const date = new Date(inputEl.value);
            onChange(date.toISOString());
        }
    };
    inputEl.addEventListener('input', handleInput);

    container.appendChild(inputEl);

    return {
        element: container,
        getValue: () => {
            if (inputEl.value === '') return null;
            const date = new Date(inputEl.value);
            return date.toISOString();
        },
        setValue: (value: unknown) => {
            if (value == null || value === '') {
                inputEl.value = '';
            } else {
                const date = parseDate(value);
                if (date) {
                    inputEl.value = formatDateForInput(date, field.withTime);
                }
            }
        },
        validate: async () => true,
        focus: () => inputEl.focus(),
        dispose: () => {
            inputEl.removeEventListener('input', handleInput);
        },
    };
}

/**
 * Parse a value into a Date object.
 */
function parseDate(value: unknown): Date | null {
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === 'string' || typeof value === 'number') {
        const date = new Date(value);
        return isNaN(date.getTime()) ? null : date;
    }
    return null;
}

/**
 * Format a date for input[type=date] or input[type=datetime-local].
 */
function formatDateForInput(date: Date, withTime?: boolean): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    if (withTime) {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    return `${year}-${month}-${day}`;
}
